import {
  Box,
  Table as ExportTable,
  Pagination,
  Icon,
  MaterialSymbol,
  usePagination,
  useUpdateQueryParams,
  TransactionTypesOptions,
  TransactionsLists,
  type Timestamp,
  type Timezone,
  type TransactionHistoryStatus,
} from 'vopay';
import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { format, subWeeks, isAfter, parseISO, getTime, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, TableFiltersContainer, CopyButton } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import useFetchTransactionsHistory from '#shared/hooks/useApi/useFetchTransactionsHistory';
import useAccountBalances from '#shared/hooks/useApi/useAccountBalances';
import useConfirmTransaction from '#shared/hooks/useApi/useConfirmTransaction';
import TransactionHistoryProvider from './TransactionHistory.provider';
import useTransactionHistory from './useTransactionHistory';
import { TransactionsHistoryHostProps } from './types';
import TransactionSearchForm from './TransactionSearchForm';
import TransactionsExport from './TransactionsExport';
import useTableConfig from './useTableConfig';
import {
  downloadPdf,
  downloadCsv,
  downloadJson,
  downloadXls,
  downloadDoc,
  downloadTxt,
  downloadXml,
  printFile,
} from './fileDownload';
import styles from './Transactions.module.scss';

type QueryParams = {
  type: string;
  status: TransactionHistoryStatus | null;
  flagged: string;
  currency: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  settlementDate: string | null;
  includeSubAccounts: string;
};

// The Transaction History feature root (ID-13367). Chrome-less: it owns the filter bar (with the export
// control on the filter row, kept internal because export is wired to this component's own fetch/file
// logic), the on-screen and hidden-export tables, and pagination — but NOT the page title or the outer
// white card, which the host wraps around it (per the epic, so the same piece drops into different
// surroundings). The host mounts it in its route with the props it maps from its own providers
// (useTransactionsHostProps); the transaction-detail page stays a host route, reached through hostPaths.
export default function TransactionHistory(hostProps: TransactionsHistoryHostProps) {
  return (
    <TransactionHistoryProvider {...hostProps}>
      <TransactionHistoryContent />
    </TransactionHistoryProvider>
  );
}

function TransactionHistoryContent() {
  const { viewer, account, userPermissions } = useTransactionHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.TransactionHistory' });

  const [filteredList, setFilteredList] = useState<TransactionsLists[]>([]);
  // The rows the hidden export table renders and the client-side XLS/DOC/TXT/XML/print exports scrape.
  // Set synchronously (flushSync) right before a scrape so the DOM is populated before it is read.
  const [exportTransactions, setExportTransactions] = useState<TransactionsLists[]>([]);

  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const currentTimeZone = useMemo(
    () => (queryParams.get('timeZone') as Timezone) || viewer.timezone || 'UTC',
    [queryParams],
  );

  // Guard the URL date params: they are fed straight to parseISO -> getTime/format downstream, and
  // a hand-edited or stale link (e.g. ?startDate=lastweek) would otherwise reach format(new Date(NaN))
  // and throw a RangeError. Drop an unparseable value so the page falls back to its default range.
  const isValidISODate = (value: string | null): boolean => !!value && isValid(parseISO(value));

  const settlementDate = useMemo(() => {
    const raw = queryParams.get('settlementDate');

    return isValidISODate(raw) ? raw : null;
  }, [queryParams]);

  const formattedSettlementDate = useMemo(
    () => (settlementDate ? format(parseISO(settlementDate), 'yyyy-MM-dd') : null),
    [settlementDate],
  );

  const type = useMemo(() => queryParams.get('type') || '', [queryParams]);
  const status = useMemo(() => (queryParams.get('status') as TransactionHistoryStatus) || null, [queryParams]);
  const flagged = useMemo(() => queryParams.get('flagged') || '', [queryParams]);
  const { balances, hasFetchedBalances, hasBalancesError } = useAccountBalances();
  const currencies = useMemo(() => balances.map((balance) => balance.Currency), [balances]);
  const currency = useMemo(() => queryParams.get('currency') || 'All', [queryParams, currencies]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);

  const startDate = useMemo(() => {
    const raw = queryParams.get('startDate') || '';

    return isValidISODate(raw) ? raw : '';
  }, [queryParams]);

  const endDate = useMemo(() => {
    const raw = queryParams.get('endDate') || '';

    return isValidISODate(raw) ? raw : '';
  }, [queryParams]);

  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), currentTimeZone, 'yyyy-MM-dd'), [currentTimeZone]);

  const timeDifference = useMemo(
    () => formatInTimeZone(subWeeks(new Date(), 1), currentTimeZone, 'yyyy-MM-dd'),
    [currentTimeZone],
  );

  const filtersApplied = useMemo(() => {
    return Boolean(
      type ||
        status ||
        flagged ||
        searchTerm ||
        (currency && currency !== 'All') ||
        (currentTimeZone && currentTimeZone !== (viewer.timezone || 'UTC')) ||
        (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime),
    );
  }, [
    type,
    status,
    flagged,
    searchTerm,
    currency,
    currentTimeZone,
    viewer.timezone,
    startDate,
    endDate,
    timeDifference,
    currentDateTime,
  ]);

  const includeSubAccounts = useMemo(() => {
    const includeSubAccountsParam = queryParams.get('includeSubAccounts');

    return includeSubAccountsParam === 'true';
  }, [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const [initialLoad, setInitialLoad] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);

  const { totalRecords, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const exportFetch = useFetchTransactionsHistory();
  const { confirmTransaction } = useConfirmTransaction();

  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();

  const PER_PAGE = 20;

  const {
    pageCount,
    pageIndex,
    offset: currentOffset,
    setPageIndex,
  } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  useEffect(() => {
    // Compare as dates, not strings
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (isAfter(start, end)) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [startDate, endDate]);

  type TransactionParams = {
    startDate: string;
    endDate: string;
    status: TransactionHistoryStatus | 'All' | null;
    flagged: string;
    type: string;
    currency: string;
    hasInvalidEndDate: boolean;
    searchTerm: string;
    timeZone: string;
    offset?: number;
    includeSubAccounts: boolean;
    settlementDate?: string;
  };

  function buildRequestPayload({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    status: selectedStatus,
    flagged: isFlaggedSelected,
    type: selectedType,
    currency: selectedCurrency,
    searchTerm: searchQuery,
    timeZone,
    offset = 0,
    includeSubAccounts: withSubAccounts,
    settlementDate: selectedSettlementDate,
  }: TransactionParams) {
    return {
      StartDateTime: selectedStartDate,
      EndDateTime: selectedEndDate,
      TransactionStatus: selectedStatus === null || selectedStatus === 'All' ? undefined : selectedStatus,
      IsFlagged: isFlaggedSelected === 'All' || isFlaggedSelected === '' ? undefined : isFlaggedSelected === 'Yes',
      TransactionType:
        selectedType === 'All' ||
        selectedType === '' ||
        selectedType === 'Refund' ||
        selectedType === 'Auto Balance Transfer'
          ? undefined
          : selectedType,
      Currency: selectedCurrency,
      SearchText: searchQuery,
      Timezone: timeZone,
      SettlementDate: selectedSettlementDate,
      Limit: PER_PAGE,
      Offset: offset,
      IncludeSubaccountTransactions: withSubAccounts,
    };
  }

  async function fetchTransactionList(params: TransactionParams) {
    if (params.hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    // TODO(ID-follow-up): replace the fullscreen blocking loader with a localized table skeleton
    // (loading-conventions) in a follow-up — this preserves today's behaviour, it isn't the target.
    blockingLoader.show();

    try {
      const res = await fetchTransactionsHistory(buildRequestPayload(params));

      setFilteredList(
        res.Transactions.map((transaction) => ({
          ...transaction,
          TransactionType: ModifyType(transaction.TransactionType, transaction.Notes),
        })),
      );

      setInitialLoad(true);
    } catch (err) {
      // The hook already logged; surface it to the user.
      const msg =
        err instanceof Error
          ? err.message
          : t('Errors.fetch', { defaultValue: 'An error occurred while fetching transaction history' });

      alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), msg);
    } finally {
      blockingLoader.hide();
    }
  }

  // Surface a balances-fetch failure the same way every other fetch on this page does — an
  // error dialog — instead of leaving the page blank.
  useEffect(() => {
    if (hasBalancesError) {
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Error' }),
        t('Errors.fetchBalances', { defaultValue: 'An error occurred while fetching account balances' }),
      );
    }
  }, [hasBalancesError]);

  useEffect(() => {
    // Fetch once the balances request has settled, whether it returned rows, an empty list, or
    // an error — keying on the count alone could never fire for an account with no balances.
    if (!hasFetchedBalances) return;
    const formattedStartDate = startDate ? format(parseISO(startDate), 'yyyy-MM-dd') : timeDifference;
    const formattedEndDate = endDate ? format(parseISO(endDate), 'yyyy-MM-dd') : currentDateTime;

    updateQueryParams({
      type,
      status,
      flagged,
      currency,
      searchTerm,
      includeSubAccounts: includeSubAccounts ? 'true' : 'false',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      timeZone: currentTimeZone,
      settlementDate: formattedSettlementDate,
    });

    fetchTransactionList({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      status,
      flagged,
      type,
      currency,
      hasInvalidEndDate: isAfter(parseISO(startDate), parseISO(endDate)),
      searchTerm,
      includeSubAccounts,
      timeZone: currentTimeZone,
      settlementDate: formattedSettlementDate || undefined,
    });
  }, [hasFetchedBalances]);

  function buildExportOptions() {
    return {
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: currentTimeZone,
      TransactionStatus: status === null || status === 'All' ? undefined : status,
      IsFlagged: flagged === 'All' || flagged === '' ? undefined : flagged === 'Yes',
      TransactionType:
        type === 'All' || type === '' || type === 'Refund' || type === 'Auto Balance Transfer' ? undefined : type,
      Currency: currency,
      SearchText: searchTerm,
      SettlementDate: formattedSettlementDate || undefined,
      ExportParams: {
        IncludeSubAccounts: includeSubAccounts,
      },
      IncludeSubaccountTransactions: includeSubAccounts,
    };
  }

  // Fetch the export rows, drop them into the hidden export table synchronously, then scrape its DOM.
  async function scrapeExport(run: () => void) {
    const res = await exportFetch.fetchTransactionsHistory({ ...buildExportOptions() });

    flushSync(() => {
      setExportTransactions(res.Transactions);
    });

    run();

    return res;
  }

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const exportHandlers: Record<typeof exportType, () => Promise<unknown>> = {
      // pdf/csv come back as a file on the response — no DOM scrape, so they resolve directly now instead
      // of through a response effect + isFileExportPending flag.
      pdf: async () => {
        const res = await exportFetch.fetchTransactionsHistory({ ...buildExportOptions(), ExportPDF: true });
        if (res.PDFFile) downloadPdf(res.PDFFile, 'transactions');
      },
      csv: async () => {
        const res = await exportFetch.fetchTransactionsHistory({ ...buildExportOptions(), ExportCSV: true });
        if (res.CSVFile) downloadCsv(res.CSVFile, 'transactions');
      },
      xls: () => scrapeExport(() => downloadXls('export-transaction-history-table', 'transactions')),
      doc: () => scrapeExport(() => downloadDoc('export-transaction-history-table', 'transactions')),
      text: () => scrapeExport(() => downloadTxt('export-transaction-history-table', 'transactions')),
      xml: () => scrapeExport(() => downloadXml('export-transaction-history-table', 'transactions')),
      print: () => scrapeExport(() => printFile('export-transaction-history-table')),
      json: async () => {
        const res = await exportFetch.fetchTransactionsHistory({ ...buildExportOptions() });
        downloadJson({ Transactions: res.Transactions }, 'transactions');
      },
    };

    blockingLoader.show();

    try {
      await exportHandlers[exportType]();
    } catch (err) {
      // The hook already logged; surface it to the user.
      const msg =
        err instanceof Error
          ? err.message
          : t('Errors.export', { defaultValue: 'An error occurred while exporting transactions' });

      alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), msg);
    } finally {
      blockingLoader.hide();
    }
  };

  const onFlagClick = async (transactionIdToFlag: string, flag: string) => {
    const result = await alertDialog.open(
      t('ConfirmRemoveFlagDialog.title', { defaultValue: 'Flag Information' }),
      <Box gapSize="16px" jc="center" ai="center">
        {flag}
        <CopyButton content={flag} />
      </Box>,
      {
        top: <MaterialSymbol icon="flag" color="#BD3A2D" size="104px" fillAmount={1} />,
        abortLabel: t('ConfirmRemoveFlagDialog.close', { defaultValue: 'Close' }),
        confirmLabel: t('ConfirmRemoveFlagDialog.removeFlag', { defaultValue: 'Remove Flag' }),
      },
    );

    if (result !== 'confirm') return;

    blockingLoader.show();

    try {
      await confirmTransaction({ TransactionID: transactionIdToFlag });

      await fetchTransactionList({
        startDate,
        endDate,
        status,
        flagged,
        type,
        currency,
        hasInvalidEndDate: startDate > endDate,
        searchTerm,
        timeZone: currentTimeZone,
        includeSubAccounts,
        offset: currentOffset,
        settlementDate: formattedSettlementDate || undefined,
      });
    } catch (err) {
      // The hook already logged; surface it to the user.
      const msg =
        err instanceof Error
          ? err.message
          : t('Errors.confirm', { defaultValue: 'An error occurred while updating the transaction' });

      alertDialog.error(
        t('ConfirmRemoveFlagDialog.errorTitle', { defaultValue: 'Error! The flag was not removed' }),
        msg,
      );
    } finally {
      blockingLoader.hide();
    }
  };

  const { tableProps, exportTableProps } = useTableConfig({
    filteredList,
    exportTransactions,
    includeSubAccounts,
    onFlagClick,
    // The export table is a hidden, scrape-only DOM node (display:none), so its per-row remove-flag button
    // is never interactable; the real remove-flag flow is the flag button in the visible table above.
    onRemoveFlagClick: () => {},
  });

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('Transactions.searchPrompt') : t('Transactions.noRecords');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.transactionTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}

          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      <Box flx="col" gap="sm">
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <Box jc="fe" ai="center" gapSize="8px">
            <TransactionsExport onExport={handleExport} />
            <TableFiltersContainer.Trigger />
          </Box>

          <TableFiltersContainer.Content>
            <TransactionSearchForm
              type={type}
              status={status}
              flagged={flagged}
              currency={currency}
              searchTerm={searchTerm}
              timeZone={currentTimeZone}
              balances={balances}
              country={account.country}
              subAccountPermissions={account.subAccountPermissions}
              canViewSubaccounts={userPermissions.canViewSubaccounts}
              includeSubAccounts={includeSubAccounts}
              settlementDate={settlementDate ? (getTime(parseISO(settlementDate)) as Timestamp) : undefined}
              initialTimeRange={[
                getTime(startDate ? parseISO(startDate) : parseISO(timeDifference)) as Timestamp,
                getTime(endDate ? parseISO(endDate) : parseISO(currentDateTime)) as Timestamp,
              ]}
              hasInvalidEndDate={hasInvalidEndDate}
              shouldShowEndDateError={shouldShowEndDateError}
              setShouldShowEndDateError={setShouldShowEndDateError}
              onSearch={(searchParams) => {
                const offset = setPageIndex(0);
                const formattedStartDate = format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd');
                const formattedEndDate = format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd');

                const nextFormattedSettlementDate = searchParams.settlementDate
                  ? format(new Date(searchParams.settlementDate), 'yyyy-MM-dd')
                  : undefined;

                updateQueryParams({
                  type: searchParams.type,
                  status: searchParams.status,
                  flagged: searchParams.flagged,
                  currency: searchParams.currency,
                  searchTerm: searchParams.searchTerm,
                  startDate: formattedStartDate,
                  endDate: formattedEndDate,
                  timeZone: searchParams.timeZone,
                  settlementDate: nextFormattedSettlementDate,
                  includeSubAccounts: searchParams.includeSubAccounts ? 'true' : 'false',
                });

                fetchTransactionList({
                  startDate: formattedStartDate,
                  endDate: formattedEndDate,
                  status: searchParams.status,
                  flagged: searchParams.flagged,
                  type: searchParams.type,
                  currency: searchParams.currency,
                  hasInvalidEndDate: isAfter(new Date(searchParams.timeRange[0]), new Date(searchParams.timeRange[1])),
                  settlementDate: nextFormattedSettlementDate,
                  searchTerm: searchParams.searchTerm,
                  timeZone: searchParams.timeZone,
                  offset,
                  includeSubAccounts: searchParams.includeSubAccounts,
                });
              }}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
      </Box>

      {exportTableProps.rows.length < 7000 && (
        <div className={styles.tableContainer} style={{ display: 'none' }} id="exportTransactionTable">
          <ExportTable {...exportTableProps} />
        </div>
      )}

      <Box>
        <Table {...tableProps} />
      </Box>

      <EmptyOrNoResults />

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);

              fetchTransactionList({
                startDate,
                endDate,
                status,
                flagged,
                type,
                currency,
                hasInvalidEndDate: startDate > endDate,
                searchTerm,
                timeZone: currentTimeZone,
                offset,
                includeSubAccounts,
                settlementDate: formattedSettlementDate || undefined,
              });
            }}
          />
        )}
      </Box>
    </>
  );
}

function ModifyType(transactionType: TransactionTypesOptions, notes: string): TransactionTypesOptions {
  if (
    (transactionType === 'EFT Withdrawal' || transactionType === 'ACH Withdrawal') &&
    notes === 'Scheduled Auto Balance Transfer'
  ) {
    return 'Auto Balance Transfer';
  }

  return transactionType;
}
